/* global debounce */

if (!customElements.get('quick-order-list')) {
  class QuickOrderList extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('qol-line-item-status');
      this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
        .reduce((total, quantityInput) => total + parseInt(quantityInput.value, 10), 0);

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;

      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0, evt.target.dataset.variantId, evt.target);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      if (evt.target.dataset.index) {
        this.updateQuantity(
          evt.target.dataset.index,
          evt.target.value,
          evt.target.dataset.variantId,
          evt.target
        );
      } else {
        this.addToCart(evt.target.dataset.variantId, evt.target.value);
      }
    }

    getCartLineFulfillmentMode(item, fallbackMode = 'delivery') {
      const resolvedMode = theme?.fulfillmentModeUtil?.getLineFulfillmentMode?.(item, fallbackMode);
      if (resolvedMode === 'click_collect') return 'click_collect';

      if (resolvedMode === 'delivery') return 'delivery';

      return fallbackMode === 'click_collect' ? 'click_collect' : 'delivery';
    }

    normalizeCartStore(store) {
      const normalized = window.StoreSelector?.normalizeStore?.(store);
      if (normalized?.id) return normalized;

      if (!store?.id) return null;

      return {
        id: String(store.id),
        name: String(store.name || ''),
        address: String(store.address || ''),
        phone: String(store.phone || store.store_phone || store.storePhone || ''),
        salonPhone: String(store.salonPhone || store.salon_phone || store.salonPhoneNumber || '')
      };
    }

    normalizeCartStoreList(stores) {
      const normalizedStores = theme?.fulfillmentModeUtil?.normalizeStoreList?.(stores);
      if (Array.isArray(normalizedStores)) return normalizedStores;

      const list = Array.isArray(stores) ? stores : [];
      const seen = new Set();
      return list.reduce((result, store) => {
        const normalized = typeof store === 'string' || typeof store === 'number'
          ? this.normalizeCartStore({ id: store })
          : this.normalizeCartStore(store);
        const storeId = String(normalized?.id || '').trim();
        if (!storeId || seen.has(storeId)) return result;

        seen.add(storeId);
        result.push(normalized);
        return result;
      }, []);
    }

    addCartStore(stores, store) {
      const list = this.normalizeCartStoreList(stores);
      const normalized = this.normalizeCartStore(store);
      const storeId = String(normalized?.id || '').trim();
      if (!storeId || list.some((item) => String(item.id) === storeId)) return list;

      return [...list, normalized];
    }

    getCartInfoStores(cart) {
      const stores = theme?.fulfillmentModeUtil?.getCartInfoStores?.(cart);
      if (Array.isArray(stores)) return this.normalizeCartStoreList(stores);

      const cartInfo = theme?.fulfillmentModeUtil?.parseCartInfo?.(cart?.attributes?._cart_info);
      return this.normalizeCartStoreList(cartInfo?.stores || []);
    }

    getCartAttributePayload(mode, store, options = {}) {
      const normalizedMode = mode === 'mixed_mode'
        ? 'mixed_mode'
        : mode === 'click_collect'
          ? 'click_collect'
          : 'delivery';
      const selectedStore = normalizedMode === 'click_collect' && store?.id
        ? this.normalizeCartStore(store)
        : null;
      const stores = this.normalizeCartStoreList(options.stores);
      const cartInfo = theme?.fulfillmentModeUtil?.stringifyCartInfo
        ? theme.fulfillmentModeUtil.stringifyCartInfo(normalizedMode, selectedStore, { stores })
        : JSON.stringify({
            version: 1,
            mode: normalizedMode,
            store_id: selectedStore?.id || '',
            store_name: selectedStore?.name || '',
            store_address: selectedStore?.address || '',
            store_phone: selectedStore?.phone || '',
            salon_phone: selectedStore?.salonPhone || '',
            store_ids: this.normalizeCartStoreList([...stores, selectedStore].filter(Boolean))
              .map((item) => item.id)
              .join('|')
          });

      return { _cart_info: cartInfo };
    }

    cartAttributesMatch(cart, mode, store, options = {}) {
      const expected = this.getCartAttributePayload(mode, store, options);
      const attributes = cart?.attributes || {};

      /* Compare the fields that decide fulfillment, not the raw string. `_cart_info`
         also carries values that change on their own - the trace id and the
         selection timestamp - and comparing the whole string would report a
         mismatch on every add to cart, rewriting the attribute and every cart line
         for nothing. */
      return Object.entries(expected).every(([key, value]) => {
        if (key !== '_cart_info') return String(attributes[key] || '') === String(value || '');

        const parse = (raw) => {
          try {
            const parsed = JSON.parse(raw || '{}');
            return [parsed?.mode || '', parsed?.store_id || '', parsed?.store_ids || ''].join('|');
          } catch {
            return '';
          }
        };

        return parse(attributes[key]) === parse(value);
      });
    }

    async syncCartAttributes(mode, store, options = {}) {
      if (!theme?.routes?.cartUpdate) return { ok: false, cart: null };

      const body = {
        attributes: this.getCartAttributePayload(mode, store, options)
      };
      const sections = Array.isArray(options.sections)
        ? [...new Set(options.sections.filter(Boolean))]
        : [];
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      let cart = null;
      try {
        cart = await response.json();
      } catch {
        // no-op
      }

      return { ok: response.ok, cart };
    }

    async addToCart(variantId, quantity) {
      let data;
      try {
        // Remove old errors
        this.querySelectorAll('.alert').forEach((alert) => alert.remove());
        this.classList.add('pointer-events-none');

        const fulfillmentUtil = theme.fulfillmentModeUtil;
        const mixedFulfillmentError = theme.strings.storeSelector?.addToCartMixedFulfillmentError
          || theme.strings.cartError;
        const missingStoreError = theme.strings.storeSelector?.addToCartSelectStoreError
          || mixedFulfillmentError;
        if (!fulfillmentUtil?.resolveAddContext) throw new Error(theme.strings.cartError);

        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!cartResponse.ok) throw new Error(String(cartResponse.status));

        const cartData = await cartResponse.json();
        const storedSelection = window.StoreSelector?.readSelection?.() || null;
        const cartResolvedMode = fulfillmentUtil.getResolvedCartMode(cartData);
        const cartResolvedStore = fulfillmentUtil.getStoreFromCart(cartData, cartResolvedMode);

        const addContext = fulfillmentUtil.resolveAddContext({
          cart: cartData,
          requestedMode: storedSelection?.mode || cartResolvedMode,
          requestedStore: storedSelection?.store || cartResolvedStore
        });
        const shouldForceConciliation = Boolean(addContext.conflict?.hasConflict);
        if (!addContext.ok) {
          throw new Error(addContext.reason === 'missing_store' ? missingStoreError : mixedFulfillmentError);
        }

        const selectedStore = addContext.mode === 'click_collect' && addContext.store?.id
          ? addContext.store
          : null;
        const representedStores = selectedStore
          ? this.addCartStore(this.getCartInfoStores(cartData), selectedStore)
          : this.getCartInfoStores(cartData);
        const syncMode = shouldForceConciliation ? 'mixed_mode' : addContext.mode;
        const syncStore = syncMode === 'click_collect' ? selectedStore : null;
        const syncStores = syncMode === 'mixed_mode'
          ? representedStores
          : syncMode === 'click_collect'
            ? this.addCartStore([], selectedStore)
            : [];
        const sectionsToRender = this.getSectionsToRender();
        if (this.cartDrawer) {
          const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
          sectionsToRender.push({
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '#cart-drawer'
          });
        }

        const shouldSyncCartAttributes = !this.cartAttributesMatch(cartData, syncMode, syncStore, { stores: syncStores });
        const requestedQuantity = Math.max(0, Number(quantity) || 0);
        const row = this.querySelector(`[data-variant-id="${variantId}"]`);
        const deliveryQuantityLimit = Number(row?.dataset.deliveryInventoryQuantity || '');

        if (addContext.mode === 'delivery' && Number.isFinite(deliveryQuantityLimit)) {
          const fallbackMode = fulfillmentUtil.getCartFallbackMode(cartData);
          const existingDeliveryQuantity = cartData.items.reduce((total, item) => {
            if (Number(item.variant_id) !== Number(variantId)) return total;

            if (this.getCartLineFulfillmentMode(item, fallbackMode) !== 'delivery') return total;

            return total + (Number(item.quantity) || 0);
          }, 0);

          if (existingDeliveryQuantity + requestedQuantity > deliveryQuantityLimit) {
            throw new Error(theme.strings.cartQtyError.replace('[quantity]', deliveryQuantityLimit));
          }
        }

        const requestData = {
          items: [
            {
              id: variantId,
              quantity: requestedQuantity
            }
          ],
          sections_url: window.location.pathname,
          sections: sectionsToRender.map((section) => section.section)
        };

        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        data = await response.json();
        if (!response.ok) throw new Error(response);

        let renderData = data;
        if (shouldSyncCartAttributes) {
          const cartAttributeSync = await this.syncCartAttributes(syncMode, syncStore, {
            stores: syncStores,
            sections: sectionsToRender.map((section) => section.section)
          });
          if (cartAttributeSync.ok && cartAttributeSync.cart?.items?.length) {
            window.StoreSelector?.noteCartBinding?.(syncMode === 'click_collect' ? syncStore : null);
          }

          if (cartAttributeSync.ok && cartAttributeSync.cart?.sections) {
            renderData = cartAttributeSync.cart;
          }
        }

        if (addContext.mode === 'click_collect' && addContext.store?.id) {
          window.StoreSelector?.writeSelection?.(addContext.mode, addContext.store);
        }

        if (shouldForceConciliation) {
          try {
            window.sessionStorage.setItem('cart-click-collect-conciliation-pending', '1');
          } catch {
            // no-op
          }
        }

        sectionsToRender.forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = QuickOrderList
            .getElementHTML(renderData.sections[section.section], section.selector);
        });

        this.dispatchEvent(new CustomEvent('on:quick-order-list:update', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId
          }
        }));
      } catch (error) {
        console.error(error); // eslint-disable-line

        // Show errors
        const item = this.querySelector(`[data-variant-id="${variantId}"] .cart-item__details`);
        const msg = document.createElement('div');
        msg.classList.add('alert', 'mt-6', 'bg-error-bg', 'text-error-text');
        msg.textContent = data?.description || data?.message || error?.message || String(error);
        item.appendChild(msg);
      } finally {
        this.classList.remove('pointer-events-none');
      }
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} variantId - Variant id being updated.
     * @param {object} quantityElem - The quantity input.
     */
    async updateQuantity(line, quantity, variantId, quantityElem) {
      this.enableLoading();

      const sectionsToRender = this.getSectionsToRender();

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sectionsToRender.push({
          id: 'cart-drawer',
          section: cartDrawerId,
          selector: '#cart-drawer'
        });
      }

      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToRender.map((section) => section.section),
        sections_url: window.location.pathname
      });

      let data;

      try {
        // Remove old errors
        this.querySelectorAll('.alert').forEach((alert) => alert.remove());
        const row = quantityElem.closest('.cart-item');
        const requestedQuantity = Math.max(0, Number(quantity) || 0);
        const maxQuantity = quantityElem.max === '' ? null : Number(quantityElem.max);

        if (
          row?.dataset.fulfillmentMode === 'delivery'
          && Number.isFinite(maxQuantity)
          && requestedQuantity > maxQuantity
        ) {
          throw new Error(theme.strings.cartQtyError.replace('[quantity]', maxQuantity));
        }

        const response = await fetch(`${theme.routes.cartChange}`, this.fetchRequestOpts);
        data = await response.json();
        if (!response.ok) throw new Error(response);

        sectionsToRender.forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = QuickOrderList
            .getElementHTML(data.sections[section.section], section.selector);
        });

        this.dispatchEvent(new CustomEvent('on:quick-order-list:update', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId
          }
        }));

        if (!this.closest('quick-add-drawer')) {
          this.disableLoading();
        }
      } catch (error) {
        console.error(error); // eslint-disable-line

        // Show errors
        const item = this.querySelector(`[data-variant-id="${variantId}"] .cart-item__qty`);
        const msg = document.createElement('div');
        msg.classList.add('alert', 'mt-6', 'bg-error-bg', 'text-error-text');
        msg.textContent = data.errors ? data.errors : error;
        item.appendChild(msg);

        // Update the quantity element
        quantityElem.value = quantityElem.dataset.initialValue;
        quantityElem.closest('quantity-input').currentQty = quantityElem.dataset.initialValue;

        this.disableLoading();
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: `quick-order-list_${this.dataset.section}`,
          section: this.dataset.section,
          selector: `#quick-order-list_${this.dataset.section}`
        },
        {
          id: 'free-shipping-notice',
          section: 'cart-drawer',
          selector: '.free-shipping-notice'
        }
      ];
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     */
    enableLoading() {
      this.classList.add('pointer-events-none');
      document.activeElement.blur();
      this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Stops the loading state
     */
    disableLoading() {
      this.classList.remove('pointer-events-none');
    }
  }

  customElements.define('quick-order-list', QuickOrderList);
}
